import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem, Card, CardContent
} from '@mui/material';
import { format } from 'date-fns';
import { Expense, AdvancePettyCash } from '../types/accounting';
import { accountingService } from '../services/accountingService';
import { projectService } from '../services/projectService';
import { Project } from '../types/project';
import AddExpenseModal from '../components/AddExpenseModal';
import { useAuth } from '../hooks/UserContext';
import MyExpenseDetailsModal from '../components/MyExpenseDetailsModal';

const MyExpensesPage: React.FC = () => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [pettyCash, setPettyCash] = useState<AdvancePettyCash[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);

  // State for filters
  const [projectFilter, setProjectFilter] = useState('');

  // State for filter dropdown data
  const [projects, setProjects] = useState<Project[]>([]);
  
  const { user } = useAuth();

  // Calculate summary totals
  const summary = useMemo(() => {
    const totalPaid = expenses
      .filter(exp => exp.status?.toLowerCase() === 'paid')
      .reduce((sum, exp) => sum + parseFloat(exp.amount), 0);
    
    const totalPayable = expenses
      .filter(exp => exp.status?.toLowerCase() === 'payable')
      .reduce((sum, exp) => sum + parseFloat(exp.amount), 0);
    
    const totalExpense = expenses
      .reduce((sum, exp) => sum + parseFloat(exp.amount), 0);

    const totalReceived = pettyCash.reduce((sum, pc) => sum + parseFloat(pc.amount), 0);
    
    const myBalance = totalReceived - totalPaid;

    const totalReceivable = Math.max(0, totalPayable - myBalance);
    
    return { totalPaid, totalPayable, totalExpense, totalReceived, myBalance, totalReceivable };
  }, [expenses, pettyCash]);

  const fetchExpenses = async (params: Parameters<typeof accountingService.getExpenses>[0]) => {
    try {
      setLoading(true);
      const expenseData = await accountingService.getExpenses(params);
      setExpenses(expenseData);
      
      if (user) {
        const pettyCashData = await accountingService.getAdvancePettyCashForUser(user.id);
        setPettyCash(pettyCashData);
      }

    } catch (err) {
      setError('Failed to fetch expenses or petty cash.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const projectData = await projectService.getProjects();
        setProjects(projectData);
      } catch (err) {
        console.error("Failed to fetch filter data", err);
      }
    };
    fetchFilterData();
  }, []);

  useEffect(() => {
    if (user) {
      fetchExpenses({ project: projectFilter, submitted_by: String(user.id) });
    }
  }, [projectFilter, user]);

  const handleSave = () => {
    if(user) {
        fetchExpenses({ project: projectFilter, submitted_by: String(user.id) });
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">My Expenses</Typography>
        <Button variant="contained" onClick={() => setIsAddModalOpen(true)}>Add Expense</Button>
      </Box>

      {/* Summary Section */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
        {/* Left Card */}
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Account Summary</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1" color="text.secondary">Total Expense:</Typography>
              <Typography variant="body1" color="primary.main">${summary.totalExpense.toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1" color="text.secondary">Total Received:</Typography>
              <Typography variant="body1" color="info.main">${summary.totalReceived.toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body1" color="text.secondary">My Balance:</Typography>
              <Typography variant="body1" color={summary.myBalance >= 0 ? 'success.main' : 'error.main'}>
                ${summary.myBalance.toFixed(2)}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Right Card */}
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Payment Summary</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1" color="text.secondary">Total Paid:</Typography>
              <Typography variant="body1" color="success.main">${summary.totalPaid.toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1" color="text.secondary">Total Payable:</Typography>
              <Typography variant="body1" color="warning.main">${summary.totalPayable.toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body1" color="text.secondary">Total Receivable:</Typography>
              <Typography variant="body1" color="error.main">${summary.totalReceivable.toFixed(2)}</Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Box sx={{ flex: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Filter by Project</InputLabel>
            <Select value={projectFilter} label="Filter by Project" onChange={(e) => setProjectFilter(e.target.value as string)}>
              <MenuItem value=""><em>All Projects</em></MenuItem>
              {projects.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' } }}>
        {/* Expenses Table */}
        <Box sx={{ flex: 2 }}>
          <Typography variant="h6" gutterBottom>My Expense Entries</Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Expense Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expenses.map((expense) => (
                  <TableRow
                    key={expense.id}
                    hover
                    onClick={() => {
                      setSelectedExpense(expense);
                      setIsDetailsModalOpen(true);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <TableCell>{format(new Date(expense.date), 'MMM dd, yyyy')}</TableCell>
                    <TableCell>{expense.project_details?.name || 'N/A'}</TableCell>
                    <TableCell>{expense.expense_type}</TableCell>
                    <TableCell>{expense.status}</TableCell>
                    <TableCell align="right">${parseFloat(expense.amount).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Petty Cash Table */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" gutterBottom>Petty Cash Received</Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pettyCash.map((pc) => (
                  <TableRow key={pc.id}>
                    <TableCell>{format(new Date(pc.date), 'MMM dd, yyyy')}</TableCell>
                    <TableCell align="right">${parseFloat(pc.amount).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      <AddExpenseModal open={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onSave={handleSave} />
      <MyExpenseDetailsModal open={isDetailsModalOpen} onClose={() => setIsDetailsModalOpen(false)} onSave={handleSave} expense={selectedExpense} />
    </Box>
  );
};

export default MyExpensesPage;